import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3 } from 'lucide-react';
import './AuditLogs.css';

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const response = await axios.get('/api/logs');
      setLogs(response.data || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getOperationIcon = (operation) => {
    const icons = {
      upload: '📤',
      extract: '⚡',
      verify: '✅',
      mapping: '🗺️',
      pdf_status: '📄',
      download: '📥'
    };
    return icons[operation] || '📝';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'pending': return 'pending';
      default: return 'info';
    }
  };

  const filteredLogs = logs
    .filter(log => {
      if (filter !== 'all' && log.operation !== filter) return false;
      if (searchTerm && !log.message?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const operations = [...new Set(logs.map(log => log.operation))];

  return (
    <div className="audit-logs">
      <div className="page-header">
        <div>
          <h1>Audit Logs</h1>
          <p>Track all system activities and operations</p>
        </div>
      </div>

      <div className="logs-controls">
        <input
          type="text"
          placeholder="Search logs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
          <option value="all">All Operations</option>
          {operations.map(op => (
            <option key={op} value={op}>
              {op.charAt(0).toUpperCase() + op.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading-state">
          <BarChart3 size={48} />
          <p>Loading audit logs...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="empty-state">
          <BarChart3 size={48} />
          <p>No logs found</p>
          <small>Start using the system to see activity logs</small>
        </div>
      ) : (
        <div className="logs-list">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Operation</th>
                <th>Status</th>
                <th>Message</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, idx) => (
                <tr key={idx} className={`status-${getStatusColor(log.status)}`}>
                  <td className="time">
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="operation">
                    <span className="op-icon">{getOperationIcon(log.operation)}</span>
                    {log.operation}
                  </td>
                  <td>
                    <span className={`status-badge ${log.status}`}>
                      {log.status}
                    </span>
                  </td>
                  <td className="message">{log.message}</td>
                  <td className="duration">
                    {log.duration ? `${log.duration}ms` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && filteredLogs.length > 0 && (
        <div className="logs-summary">
          <div className="summary-item">
            <span className="summary-label">Total Operations</span>
            <span className="summary-value">{filteredLogs.length}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Success Rate</span>
            <span className="summary-value">
              {Math.round(
                (filteredLogs.filter(l => l.status === 'completed').length /
                  filteredLogs.length) * 100
              )}%
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Avg Duration</span>
            <span className="summary-value">
              {Math.round(
                filteredLogs.reduce((sum, l) => sum + (l.duration || 0), 0) /
                  filteredLogs.length
              )}ms
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLogs;
